import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import HeaderProfileSection from "../HeaderProfileSection";
import MyWalletSidebar from "../MyWalletSidebar";
import {
  useReferralLeaderboard,
  useReferralSummary,
} from "../../hooks/useReferralData";
import { useSidebarData } from "../../hooks/useSidebarData";
import { getUserStateNameFromSidebar } from "../../questionBank/stateUtils";
import { REFERRAL_ROUTES } from "./referralRoutes";
import { ReferralLeaderboardSkeleton } from "./ReferralShimmers";
import fallbackAvatar from "../../assets/images/profile1.png";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QuestionBankList.css";
import "../quizzes/QuizLeaderboard.css";
import "../rapidFire/RapidFireLeaderBoard.css";
import "./ReferralPreviewLayout.css";
import crown from "../../assets/images/crown1.png";
const formatCoins = (value) =>
  new Intl.NumberFormat("en-IN").format(Number(value) || 0);
const isOverallFilter = (filter) =>
  /^(overall|all time|all)$/i.test(String(filter?.name || "").trim()) ||
  /^(overall|all_time|all)$/i.test(String(filter?.key || "").trim());

function PlayerImage({ player, className }) {
  return (
    <img
      className={className}
      src={player?.image || fallbackAvatar}
      alt=""
      onError={(event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src = fallbackAvatar;
      }}
    />
  );
}

function PodiumPlayer({ player, place }) {
  if (!player) return null;
  return (
    <div className={`top-player ${place}`}>
      <div className="badge-wrapper">
        <PlayerImage player={player} className="top-badge" />
        <span className={`rank-circle${place === "third" ? " grey" : ""}`}>
          {player.rank || (place === "first" ? 1 : place === "second" ? 2 : 3)}
        </span>
        {place === "first" && (
          <span className="winner-str">
            <span><span className="winner-crown crown-position">
                 <img src={crown} alt="" />
                 </span>
            </span>
          </span>
        )}
      </div>
      <div className="player-name">{player.name}</div>
      <div className="player-score">{formatCoins(player.score)}</div>
    </div>
  );
}

export default function ReferralLeaderboard() {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState(null);
  const rankListRef = useRef(null);
  const summaryQuery = useReferralSummary();
  const sidebarQuery = useSidebarData();
  const baseQuery = useReferralLeaderboard(null);
  const filteredQuery = useReferralLeaderboard(
    selectedFilter,
    Boolean(selectedFilter),
  );
  const query = selectedFilter ? filteredQuery : baseQuery;
  const pages = query.data?.pages || [];
  const firstPage = pages[0];
  const filters =
    baseQuery.data?.pages?.[0]?.filters || firstPage?.filters || [];
  const displayFilters = filters.some(isOverallFilter)
    ? filters
    : [{ key: "__overall__", name: "Overall" }, ...filters];
  const topThree = firstPage?.topThree || [];
  const [first, second, third] = topThree;
  const students = useMemo(() => {
    const seen = new Set();
    return (query.data?.pages || []).flatMap((page) =>
      (page.students || []).filter((student) => {
        const key = `${student.rank}-${student.studentId ?? student.name}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    );
  }, [query.data]);
  const currentStudent = firstPage?.currentStudent;
  const stateName =
    getUserStateNameFromSidebar(sidebarQuery.data) || "All States";

  useEffect(() => {
    const container = rankListRef.current;
    if (!container || query.isPending) return undefined;
    let requesting = false;
    const handleScroll = () => {
      if (
        requesting ||
        !query.hasNextPage ||
        query.isFetchingNextPage ||
        query.isFetchNextPageError
      )
        return;
      const remainingHeight =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (remainingHeight < 120) {
        requesting = true;
        query.fetchNextPage().finally(() => {
          requesting = false;
        });
      }
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [query, selectedFilter]);

  return (
    <>
      <style>{`
         .homeDashboard-wrapper .page-content {
          padding-right: 0;
          min-height: 0;
          height: calc(100vh - 35px);
          overflow-x: hidden;
        }

        .homeDashboard-wrapper .main-content {
          height: 100vh;
          overflow-x: hidden !important;
          box-sizing: border-box;
          padding-bottom: 0px;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }

        .layout-wrapper {
          background: #f8f9fa !important;
        }

        .page-padding {
          padding: 30px 25px 0 !important;
        }

       @media (max-width: 767px){
        .page-padding {
             padding: 30px 15px 0 !important;
        }
      `}</style>{" "}
      <HomeDashboardLayout
        showHeader={false}
        variant="referral-preview referral-leaderboard-layout"
      >
        <div className="qbl-page qbankList-page page-padding fixed-page-btn rank-leader referral-rank-page">
          <div className="start-quiz qbl-main instruction-main">
            <div className="hideProfile-page">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={() => navigate(REFERRAL_ROUTES.home)}
                    aria-label="Back to referral rewards"
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                </div>
                <div>
                  <h4 className="d-flex categories-page-title"></h4>
                </div>
                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>
            <div className="leaderboard-screen">
                  <div>
                <h1 className="qb-title">
                  Leaderboard
                </h1>

                <p className="qb-sub">
                  See how you rank among top learners.
                </p>
              </div>
              <div className="leaderboard-header">
                <div className="event-info">
                  <h1 className="event-title">Check your State Rank</h1>
                  <span className="event-date">
                    {summaryQuery.data?.category_name || "Referral Leaderboard"}
                  </span>
                </div>
                <div className="state-dropdown">
                  <div className="state-dropdown-button">
                    <span>{stateName}</span>
                  </div>
                </div>
              </div>
              {baseQuery.isPending && (
                <div
                  className="status-tabs referral-filter-tabs"
                  aria-label="Loading leaderboard filters"
                >
                  {[1, 2, 3].map((item) => (
                    <span
                      className="dashboard-skeleton referral-filter-shimmer"
                      key={item}
                    />
                  ))}
                </div>
              )}
              {!baseQuery.isPending && displayFilters.length > 0 && (
                <div
                  className="status-tabs referral-filter-tabs"
                  aria-label="Leaderboard time filters"
                >
                  {displayFilters.map((filter) => {
                    const overall = isOverallFilter(filter);
                    const active = overall
                      ? selectedFilter === null
                      : selectedFilter === filter.key;
                    return (
                      <button
                        type="button"
                        key={filter.key}
                        className={`status-tab ${active ? "active" : ""}`}
                        aria-pressed={active}
                        onClick={() => {
                          if (rankListRef.current)
                            rankListRef.current.scrollTop = 0;
                          setSelectedFilter((current) =>
                            overall || current === filter.key
                              ? null
                              : filter.key,
                          );
                        }}
                      >
                        {filter.name}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="leaderboard-content">
                <section className="leaderboard-card">
                  {query.isPending ? (
                    <ReferralLeaderboardSkeleton />
                  ) : (
                    <>
                      {query.isError && !query.isFetchNextPageError && (
                        <div className="referral-status" role="alert">
                          <p>
                            {query.error?.message ||
                              "Unable to load the leaderboard."}
                          </p>
                          <button type="button" onClick={() => query.refetch()}>
                            Retry
                          </button>
                        </div>
                      )}
                      {topThree.length > 0 && (
                        <div className="top-three">
                          <PodiumPlayer player={second} place="second" />
                          <PodiumPlayer player={first} place="first" />
                          <PodiumPlayer player={third} place="third" />
                        </div>
                      )}

                      <div className="rank-list-wrapper">
                        {" "}
                        <div className="rank-header">
                          <span className="rank-heading">RANK LIST</span>
                          <span className="performer-heading">
                            Top Performers
                          </span>
                        </div>
                        <div className="rank-list" ref={rankListRef}>
                          {students.map((student, index) => (
                            <div
                              className="rank-row"
                              key={`${student.studentId ?? "student"}-${student.rank}-${index}`}
                            >
                              <div className="rank-number">
                                {student.rank || index + 1}
                              </div>
                              <PlayerImage
                                player={student}
                                className="small-badge"
                              />
                              <div className="rank-name">{student.name}</div>
                              <div className="rank-score">
                                {formatCoins(student.score)}
                              </div>
                            </div>
                          ))}
                          {!students.length && !query.isError && (
                            <p className="leaderboard-status">No Data</p>
                          )}
                          {query.isFetchingNextPage && (
                            <div
                              className="rank-row qbl-shimmer__row"
                              aria-label="Loading more ranks"
                            >
                              <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__rank" />
                              <span className="dashboard-skeleton dashboard-skeleton--thumb qbl-shimmer__avatar" />
                              <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__name" />
                              <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__score" />
                            </div>
                          )}
                          {query.isFetchNextPageError && (
                            <div className="referral-page-retry" role="alert">
                              <span>
                                {query.error?.message ||
                                  "Couldn't load more ranks."}
                              </span>
                              <button
                                type="button"
                                onClick={() => query.fetchNextPage()}
                              >
                                Retry
                              </button>
                            </div>
                          )}
                        </div>
                        {currentStudent?.rank > 0 && (
                          <div className="rank-bottom-action">
                            <div className="current-rank-card">
                              <div className="current-rank-number">
                                {currentStudent.rank}
                              </div>
                              <div className="current-rank-avatar">
                                <PlayerImage player={currentStudent} />
                              </div>
                              <div className="current-rank-info">
                                <span>Your Current rank</span>
                                <strong>{currentStudent.name}</strong>
                              </div>
                              <div className="current-rank-score">
                                {formatCoins(currentStudent.score)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </section>
              </div>
            </div>
          </div>
        </div>
        <MyWalletSidebar withTopOffset={false} />
      </HomeDashboardLayout>
      ;
    </>
  );
}
